import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { POSITIONS, PROVINCES } from '../mock';
import { Plus, Trash2, ImagePlus, Video, Save, User, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import { playersApi, uploadsApi, absUrl } from '../lib/api';

const EMPTY = {
  full_name: '', nickname: '', position: '', age: '', province: '',
  profile_picture: '', bio: '',
  career_journey: [{ team: '', years: '', role: '' }],
  pictures: [],
  videos: [],
  available_for_offers: true,
  contact: { phone: '', email: '', whatsapp: '' },
};

export default function MyProfile() {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (user?.player_id) {
          const mine = await playersApi.get(user.player_id);
          setForm({ ...EMPTY, ...mine, contact: { ...EMPTY.contact, ...(mine.contact || {}) } });
        } else if (user?.email) {
          setForm((f) => ({ ...f, contact: { ...f.contact, email: user.email } }));
        }
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [user]);

  const isEditing = !!user?.player_id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));

  const addCareer = () => set('career_journey', [...form.career_journey, { team: '', years: '', role: '' }]);
  const removeCareer = (i) => set('career_journey', form.career_journey.filter((_, idx) => idx !== i));
  const updateCareer = (i, k, v) => set('career_journey', form.career_journey.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

  const uploadImages = async (files) => {
    const arr = [];
    for (const f of files) {
      try { const { url } = await uploadsApi.image(f); arr.push(url); } catch { /* ignore */ }
    }
    return arr;
  };

  const handlePicUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 6 - form.pictures.length;
    const toAdd = files.slice(0, remaining);
    e.target.value = '';
    setUploading(true);
    const urls = await uploadImages(toAdd);
    setUploading(false);
    set('pictures', [...form.pictures, ...urls]);
  };
  const removePic = (i) => set('pictures', form.pictures.filter((_, idx) => idx !== i));

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const { url } = await uploadsApi.image(file);
      set('profile_picture', url);
    } catch { toast({ title: 'Upload failed', variant: 'destructive' }); }
    finally { setUploading(false); }
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 5 - form.videos.length;
    const toAdd = files.slice(0, remaining);
    e.target.value = '';

    toAdd.forEach((file) => {
      const localUrl = URL.createObjectURL(file);
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      videoEl.onloadedmetadata = async () => {
        const duration = videoEl.duration;
        if (duration > 92) {
          toast({ title: 'Video too long', description: `"${file.name}" is ${Math.round(duration)}s. Max 1 min 30 sec.`, variant: 'destructive' });
          return;
        }
        setUploading(true);
        try {
          const { url } = await uploadsApi.video(file);
          const mm = Math.floor(duration / 60);
          const ss = String(Math.floor(duration % 60)).padStart(2, '0');
          setForm((f) => f.videos.length >= 5 ? f : ({ ...f, videos: [...f.videos, { title: file.name, url, duration: `${mm}:${ss}` }] }));
        } catch (err) {
          toast({ title: 'Video upload failed', description: err?.response?.data?.detail || 'Try a smaller file', variant: 'destructive' });
        } finally { setUploading(false); }
      };
      videoEl.src = localUrl;
    });
  };
  const removeVideo = (i) => set('videos', form.videos.filter((_, idx) => idx !== i));

  const canSave = useMemo(() => form.full_name && form.position && form.province && form.age && form.contact.phone && form.profile_picture, [form]);

  const handleSave = async () => {
    if (!canSave) {
      const missing = !form.profile_picture
        ? 'Please upload a profile picture.'
        : 'Please fill in name, position, age, province and phone number.';
      toast({ title: 'Missing information', description: missing, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, age: Number(form.age) };
      await playersApi.save(payload);
      await refresh();
      toast({ title: 'Profile saved', description: 'Your profile is now live in the search.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-20 flex justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin"/></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{isEditing ? 'Edit your profile' : 'Build your player profile'}</h1>
          <p className="text-gray-500 mt-1">Complete every section for maximum visibility.</p>
        </div>
        {isEditing && (
          <Link to={`/player/${user.player_id}`}><Button variant="outline">View public profile</Button></Link>
        )}
      </div>

      <div className="space-y-6">
        <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><User className="w-5 h-5 text-[color:var(--mft-green)]"/>Basic info</h2>
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="flex flex-col items-center gap-2">
              <div className="w-28 h-28 rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 overflow-hidden flex items-center justify-center">
                {form.profile_picture ? (
                  <img src={absUrl(form.profile_picture)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-gray-300" />
                )}
              </div>
              <label className="text-xs text-[color:var(--mft-green)] font-semibold cursor-pointer hover:underline">
                {form.profile_picture ? 'Change photo' : 'Upload photo*'}
                <input type="file" accept="image/*" className="hidden" onChange={handleProfilePicUpload} />
              </label>
              {!form.profile_picture && <span className="text-[10px] text-gray-500">Required</span>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1">
              <Field label="Full name*"><Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} placeholder="e.g. Thabo Mokoena" className="mft-input" /></Field>
              <Field label="Nickname"><Input value={form.nickname} onChange={(e) => set('nickname', e.target.value)} placeholder="e.g. The Wall" className="mft-input" /></Field>
              <Field label="Position*">
                <Select value={form.position} onValueChange={(v) => set('position', v)}>
                  <SelectTrigger className="mft-input"><SelectValue placeholder="Select position" /></SelectTrigger>
                  <SelectContent>{POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Age*"><Input type="number" min="14" max="55" value={form.age} onChange={(e) => set('age', e.target.value)} placeholder="e.g. 22" className="mft-input" /></Field>
              <Field label="Province*">
                <Select value={form.province} onValueChange={(v) => set('province', v)}>
                  <SelectTrigger className="mft-input"><SelectValue placeholder="Select province" /></SelectTrigger>
                  <SelectContent>{PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Available for offers?">
                <div className="flex items-center gap-3 h-10">
                  <Switch checked={form.available_for_offers} onCheckedChange={(v) => set('available_for_offers', v)} />
                  <span className="text-sm text-gray-600">{form.available_for_offers ? 'Yes, open to offers' : 'Not available'}</span>
                </div>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Short bio"><Textarea value={form.bio} onChange={(e) => set('bio', e.target.value)} placeholder="Tell scouts what makes you special..." className="mft-input min-h-[90px]" /></Field>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900">Career journey</h2>
            <Button variant="outline" size="sm" onClick={addCareer}><Plus className="w-4 h-4 mr-1"/>Add team</Button>
          </div>
          <div className="space-y-3">
            {form.career_journey.map((c, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                <div className="md:col-span-5"><Input value={c.team} onChange={(e) => updateCareer(i, 'team', e.target.value)} placeholder="Team name" className="mft-input"/></div>
                <div className="md:col-span-3"><Input value={c.years} onChange={(e) => updateCareer(i, 'years', e.target.value)} placeholder="e.g. 2020-2023" className="mft-input"/></div>
                <div className="md:col-span-3"><Input value={c.role} onChange={(e) => updateCareer(i, 'role', e.target.value)} placeholder="Role" className="mft-input"/></div>
                <div className="md:col-span-1">
                  <Button variant="ghost" size="icon" onClick={() => removeCareer(i)} disabled={form.career_journey.length === 1} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4"/></Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-gray-900">Match photos</h2>
              <p className="text-xs text-gray-500">Up to 6 photos of you in action ({form.pictures.length}/6)</p>
            </div>
            <label className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg cursor-pointer ${form.pictures.length >= 6 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[color:var(--mft-green)] text-white hover:bg-[color:var(--mft-green-dark)]'}`}>
              <ImagePlus className="w-4 h-4"/> Add photos
              <input type="file" multiple accept="image/*" className="hidden" onChange={handlePicUpload} disabled={form.pictures.length >= 6} />
            </label>
          </div>
          {form.pictures.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center text-sm text-gray-400">No photos added yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {form.pictures.map((src, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group">
                  <img src={absUrl(src)} alt="" className="w-full h-full object-cover"/>
                  <button onClick={() => removePic(i)} className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 text-red-500 opacity-0 group-hover:opacity-100 transition shadow"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-gray-900">Video clips</h2>
              <p className="text-xs text-gray-500">Up to 5 clips, max 1 min 30 sec each ({form.videos.length}/5)</p>
            </div>
            <label className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg cursor-pointer ${form.videos.length >= 5 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[color:var(--mft-green)] text-white hover:bg-[color:var(--mft-green-dark)]'}`}>
              <Video className="w-4 h-4"/> Add video
              <input type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} disabled={form.videos.length >= 5} />
            </label>
          </div>
          {form.videos.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-xl py-10 text-center text-sm text-gray-400">No videos added yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {form.videos.map((v, i) => (
                <div key={i} className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                  <div className="aspect-video bg-black">
                    {v.url && v.url.includes('youtube') ? (
                      <iframe title={v.title} src={v.url} className="w-full h-full" allowFullScreen />
                    ) : (
                      <video src={absUrl(v.url)} controls className="w-full h-full" />
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 truncate max-w-[180px]">{v.title}</div>
                      <div className="text-xs text-gray-500">{v.duration}</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeVideo(i)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4"/></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-4">Contact details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Phone*"><Input value={form.contact.phone} onChange={(e) => setContact('phone', e.target.value)} placeholder="+27 82 000 0000" className="mft-input"/></Field>
            <Field label="WhatsApp"><Input value={form.contact.whatsapp} onChange={(e) => setContact('whatsapp', e.target.value)} placeholder="+27 82 000 0000" className="mft-input"/></Field>
            <Field label="Email"><Input value={form.contact.email} onChange={(e) => setContact('email', e.target.value)} placeholder="you@example.com" className="mft-input"/></Field>
          </div>
        </section>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-end">
          {uploading && <span className="text-sm text-gray-500 inline-flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin"/> Uploading...</span>}
          <Button onClick={handleSave} disabled={saving} className="mft-btn-primary h-11 px-6 gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} {isEditing ? 'Save changes' : 'Publish profile'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-800 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
